import {
  Button,
  FieldGroup,
  FieldRow,
  FieldSet,
  Input,
  Radio,
  Section,
} from "react-corner-ds";
import { useTranslation } from "react-i18next";
import style from "./EmploymentDetails.module.css";
import { Request } from "../../../models/request/request.model";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useFlow } from "../../../hooks/useFlow/useFlow";
import { EmploymentDetailsInfo } from "../../../models/request/data/data.model";
import { FinancialInformations } from "../../../models/bank/commons/financialInformations.model";
import { store } from "../../../redux/store";
import { occupationItems } from "../../../assets/locales/utils";
import { Bank } from "../../../models/bank/bank.model";
import { Customer } from "../../../models/bank/customer/customer.model";

interface IEmploymentDetails {
  modalRecap?: boolean;
  modalCallback?: () => void;
  mockObject?: Bank;
}

const EmploymentDetails: React.FC<IEmploymentDetails> = ({
  modalRecap,
  modalCallback,
  mockObject,
}) => {
  const { t } = useTranslation();
  const { callSteploader } = useFlow();
  const [occupation, setOccupation] = useState<string>("");
  const [nextStepLoading, setNextStepLoading] = useState<boolean>(false);

  const defaultValues: EmploymentDetailsInfo = useMemo(
    () => ({
      occupation: "",
      qualification: "",
      lpp: "",
    }),
    []
  );

  const { handleSubmit, formState, control, setValue, trigger } =
    useForm<EmploymentDetailsInfo>({
      mode: "all",
      reValidateMode: "onChange",
      defaultValues: defaultValues,
    });

  const validity = useCallback(
    (error: FieldError | undefined, isTouched: boolean) =>
      !error && isTouched ? true : undefined,
    []
  );

  const collectedDataValidation = useCallback((customerStore: Customer) => {
    let triggerValidation =
      customerStore.FinancialInformation?.Occupation &&
      customerStore.FinancialInformation?.Qualification &&
      customerStore.LPP;
    return triggerValidation;
  }, []);

  useEffect(() => {
    let customerStore: Customer = mockObject?.Customer ?? {};

    if (store.getState().appConf.workflowEnabled) {
      customerStore = store.getState().pega.Data?.Bank.Customer ?? {};
    }

    if (customerStore && customerStore.FinancialInformation) {
      setValue(
        "occupation",
        customerStore.FinancialInformation.Occupation ?? ""
      );
      setValue(
        "qualification",
        customerStore.FinancialInformation.Qualification ?? ""
      );
      setValue("lpp", customerStore.LPP ? "Yes" : "No");
      setOccupation(customerStore.FinancialInformation.Occupation ?? "");
      //Trigger validation if there's customer data coming from pega
      if (collectedDataValidation(customerStore)) trigger();
    }
  }, [mockObject?.Customer, setValue, trigger]);

  const buildRequest = useCallback((data: EmploymentDetailsInfo) => {
    const a: FinancialInformations = {
      Occupation: data.occupation,
      Qualification: data.qualification,
    };

    return a;
  }, []);

  const submitHandler = async (data: EmploymentDetailsInfo) => {
    let body: Request = {
      Data: {
        FlowDirection: modalRecap ? "Still" : "Next",
        Bank: {
          Customer: {
            FinancialInformation: buildRequest(data),
            LPP: data.lpp === "Yes" ? true : false,
          },
        },
      },
      Extra: {},
    };

    setNextStepLoading(true);
    if (modalRecap) {
      await callSteploader(body, false, "EmploymentDetails");
      setNextStepLoading(false);
    } else {
      await callSteploader(body);
    }

    modalRecap && modalCallback && modalCallback();
  };

  const occupationRadio = useMemo(
    () => (
      <Controller
        control={control}
        name={"occupation"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({ field: { onChange, name, value } }) => (
          <Radio
            name={name}
            options={occupationItems(t)}
            customCssClass={style.occupation}
            onChange={(ev: any) => {
              onChange(ev);
              setOccupation(ev);
            }}
            value={value}
          />
        )}
      />
    ),
    [control, t]
  );

  const qualificationInput = useMemo(
    () => (
      <Controller
        control={control}
        name={"qualification"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            name={name}
            label={t`screens.employmentsDetail.qualification`}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={error?.message}
            maxLength={60}
            value={value}
          />
        )}
      />
    ),
    [control, t, validity]
  );

  const pensionFundRadio = useMemo(
    () => (
      <Controller
        control={control}
        name="lpp"
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({ field: { onChange, name, value } }) => (
          <Radio
            name={name}
            value={value}
            options={[
              { text: t`screens.common.yes`, _value: "Yes" },
              { text: t`screens.common.no`, _value: "No" },
            ]}
            onChange={(updateValue: any) => {
              onChange(updateValue);
            }}
          />
        )}
      />
    ),
    [control, t]
  );

  return (
    <div className="container">
      <h1>{t`screens.employmentsDetail.pageTitle`}</h1>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(submitHandler)}
        className={modalRecap ? style.modalView : ""}
      >
        <Section>
          <FieldSet>
            <FieldRow>
              <FieldGroup>{occupationRadio}</FieldGroup>
            </FieldRow>
            <FieldRow>
              <FieldGroup>{qualificationInput}</FieldGroup>
            </FieldRow>
            <>
              <hr />
              <p>
                <b>{t`screens.employmentsDetail.pensionFund`}</b>
              </p>

              <FieldRow>
                <FieldGroup>{pensionFundRadio}</FieldGroup>
              </FieldRow>
            </>
          </FieldSet>
        </Section>

        <Section>
          <FieldSet>
            <div className={style.btnHolder + " btnHolder"}>
              <Button
                text={t`screens.common.continue`}
                disabled={!formState.isValid || nextStepLoading}
                type="submit"
                icon={
                  nextStepLoading
                    ? { icon: "loader", position: "right", spin: true }
                    : undefined
                }
              />
            </div>
          </FieldSet>
        </Section>
      </form>
    </div>
  );
};

export default EmploymentDetails;
